/**
 * SectionHeader Component
 *
 * Reusable collapsible section header with icon, title, subtitle, and actions
 */

import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

export interface SectionHeaderProps {
    icon: React.ReactNode;
    iconBgGradient: string;
    iconBorderColor?: string;
    title: string;
    subtitle?: string;
    expanded: boolean;
    onToggle: () => void;
    isDarkMode: boolean;
    actions?: React.ReactNode;
    count?: number;
    disabled?: boolean;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
    icon,
    iconBgGradient,
    iconBorderColor,
    title,
    subtitle,
    expanded,
    onToggle,
    isDarkMode,
    actions,
    count,
    disabled = false,
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: disabled ? 'default' : 'pointer',
                py: 1,
                userSelect: 'none',
            }}
            onClick={disabled ? undefined : onToggle}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Icon Container */}
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: iconBgGradient,
                        border: iconBorderColor ? `2px solid ${iconBorderColor}` : 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}
                >
                    {icon}
                </Box>

                {/* Title & Subtitle */}
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                color: isDarkMode ? '#f1f5f9' : '#1e293b',
                            }}
                        >
                            {title}
                        </Typography>
                        {count !== undefined && (
                            <Box
                                sx={{
                                    bgcolor: isDarkMode ? '#334155' : '#e2e8f0',
                                    color: isDarkMode ? '#94a3b8' : '#64748b',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                }}
                            >
                                {count}
                            </Box>
                        )}
                    </Box>
                    {subtitle && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: isDarkMode ? '#94a3b8' : '#64748b',
                                fontSize: '0.85rem',
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Actions & Expand Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {actions && (
                    <Box onClick={(e) => e.stopPropagation()}>
                        {actions}
                    </Box>
                )}
                {!disabled && (
                    <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
                        <IconButton
                            size="small"
                            sx={{
                                bgcolor: isDarkMode ? '#334155' : '#f1f5f9',
                                '&:hover': {
                                    bgcolor: isDarkMode ? '#475569' : '#e2e8f0',
                                },
                            }}
                        >
                            {expanded ? (
                                <ExpandLess sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }} />
                            ) : (
                                <ExpandMore sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }} />
                            )}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

export default SectionHeader;
